import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEOHead from '../SEO/SEOHead';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const stagger = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const vancouverVerticals = [
  {
    industry: 'Film, VFX & Creative Technology',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
    description: 'Vancouver is Hollywood North and a global VFX hub. We help studios and creative technology companies build cloud rendering pipelines, AI-powered asset management, scalable infrastructure for production workloads, and data systems that handle massive media files at scale.',
    services: ['Cloud rendering pipeline architecture', 'AI-powered VFX and content generation', 'Media asset management platforms', 'Scalable storage and compute for production'],
  },
  {
    industry: 'SaaS & Technology Startups',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    description: 'Vancouver is one of Canada\'s top tech hubs with a thriving SaaS ecosystem. We provide fractional CTO services, architecture reviews, and hands-on engineering leadership to help startups scale from seed to Series B and beyond without making the technical mistakes that slow growth.',
    services: ['Fractional CTO for seed to Series B', 'Architecture reviews and tech debt remediation', 'Engineering team scaling and hiring strategy', 'Technical due diligence for fundraising'],
  },
  {
    industry: 'Mining & Natural Resources',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    description: 'British Columbia\'s mining and natural resource companies are headquartered in Vancouver but operate globally. We build the data infrastructure, remote monitoring systems, and AI-driven analytics that connect corporate offices to field operations across multiple continents.',
    services: ['Remote operations monitoring platforms', 'Environmental compliance data systems', 'Geospatial data pipeline architecture', 'AI-driven exploration analytics'],
  },
  {
    industry: 'Cleantech & Sustainability',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
    description: 'Vancouver is a global cleantech leader. We work with carbon tracking platforms, renewable energy companies, and ESG reporting firms to build the cloud infrastructure and AI systems that power the clean economy transition.',
    services: ['Carbon tracking and ESG data platforms', 'Renewable energy monitoring systems', 'Climate data analytics pipelines', 'AI-powered sustainability reporting'],
  },
];

const services = [
  {
    title: 'Fractional CTO Services',
    description: 'On-demand engineering leadership for Vancouver tech companies. From architecture decisions to board presentations, get senior technical guidance tailored to BC\'s competitive landscape.',
    link: '/services',
  },
  {
    title: 'AI & Agentic Implementation',
    description: 'Production AI systems for Vancouver businesses. LLM integration, agentic workflows, computer vision, and intelligent automation—built for real workloads, not demos.',
    link: '/services',
  },
  {
    title: 'Cloud Architecture & Migration',
    description: 'Multi-cloud expertise across AWS, GCP, and Azure Canada regions. Kubernetes orchestration, serverless patterns, and infrastructure as code for BC enterprises.',
    link: '/services',
  },
  {
    title: 'Data & Analytics',
    description: 'Modern data stack for Vancouver companies—BigQuery, Snowflake, dbt, Looker. Real-time analytics, data pipelines, and business intelligence at enterprise scale.',
    link: '/services',
  },
];

const VancouverPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="Technology Consulting Vancouver | Fractional CTO & AI Strategy"
        description="North provides fractional CTO services, AI strategy consulting, cloud architecture, and data analytics for Vancouver and British Columbia businesses. Serving film/VFX, SaaS, mining, and cleantech sectors."
        path="/vancouver"
      />

      {/* Hero Section with Top CTA */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-cream via-white to-green-50 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-emerald-100/40 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-teal-50/40 to-transparent rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 relative">
          <motion.div {...fadeIn} className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full mb-6 border border-gray-100">
              <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              <span className="text-sm font-medium text-dark-slate">Serving Vancouver & British Columbia</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-dark-slate leading-[1.05] mb-6">
              Technology Consulting{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">Vancouver</span>
            </h1>

            <p className="text-xl text-muted-grey leading-relaxed mb-8 max-w-3xl">
              Fractional CTO services, AI strategy, cloud architecture, and data analytics for Vancouver and BC businesses.
              We help tech startups, film studios, mining companies, and cleantech firms build and scale modern technology platforms.
            </p>

            {/* Top CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.a
                href="https://calendar.app.google/ube4SfQSbn6SqRCCA"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-dark-slate text-white rounded-full font-semibold text-lg hover:bg-deep-charcoal transition-colors inline-flex items-center justify-center gap-2"
              >
                Book a Strategy Call
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </motion.a>
              <Link
                to="/services"
                className="px-8 py-4 border-2 border-dark-slate text-dark-slate rounded-full font-semibold text-lg hover:bg-dark-slate hover:text-white transition-colors inline-flex items-center justify-center"
              >
                View Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeIn} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-dark-slate mb-4">
              What We Deliver in Vancouver
            </h2>
            <p className="text-lg text-muted-grey max-w-2xl mx-auto">
              Enterprise-grade technology consulting tailored to Vancouver's innovation-driven economy and diverse industry base.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((svc, i) => (
              <motion.div
                key={svc.title}
                {...stagger}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="p-8 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all"
              >
                <h3 className="text-xl font-bold text-dark-slate mb-3">{svc.title}</h3>
                <p className="text-muted-grey leading-relaxed mb-4">{svc.description}</p>
                <Link to={svc.link} className="text-dark-slate font-medium hover:text-deep-charcoal transition-colors inline-flex items-center gap-1">
                  Learn more
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Verticals */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeIn} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-dark-slate mb-4">
              Vancouver Industry Expertise
            </h2>
            <p className="text-lg text-muted-grey max-w-3xl mx-auto">
              Deep understanding of the industries that power British Columbia's economy. We build technology solutions that address the specific challenges and opportunities in each sector.
            </p>
          </motion.div>

          <div className="space-y-12">
            {vancouverVerticals.map((vertical, i) => (
              <motion.div
                key={vertical.industry}
                {...stagger}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`flex flex-col lg:flex-row gap-8 items-start p-8 rounded-2xl ${
                  i % 2 === 0 ? 'bg-white border border-gray-100' : 'bg-cream/50'
                }`}
              >
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center text-emerald-600">
                  {vertical.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-dark-slate mb-3">{vertical.industry}</h3>
                  <p className="text-muted-grey leading-relaxed mb-4">{vertical.description}</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {vertical.services.map((svc) => (
                      <div key={svc} className="flex items-start gap-2 text-sm text-dark-slate">
                        <svg className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {svc}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Vancouver Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeIn}>
              <h2 className="text-3xl md:text-4xl font-bold text-dark-slate mb-6">
                Why Vancouver Businesses Choose North
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 font-bold">1</div>
                  <div>
                    <h4 className="font-bold text-dark-slate mb-1">Tech Ecosystem Fluency</h4>
                    <p className="text-muted-grey">We understand Vancouver's startup culture, its talent market, and the unique dynamics of scaling a tech company in Canada's most competitive tech hub.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 font-bold">2</div>
                  <div>
                    <h4 className="font-bold text-dark-slate mb-1">Creative Technology Expertise</h4>
                    <p className="text-muted-grey">Vancouver is a global leader in film, VFX, and gaming. We have deep experience with the compute-intensive, data-heavy infrastructure these industries demand.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 font-bold">3</div>
                  <div>
                    <h4 className="font-bold text-dark-slate mb-1">Cross-Border Experience</h4>
                    <p className="text-muted-grey">Many Vancouver companies serve US and global markets. We architect systems that handle multi-region compliance, data residency, and international scale.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 font-bold">4</div>
                  <div>
                    <h4 className="font-bold text-dark-slate mb-1">AI-First Approach</h4>
                    <p className="text-muted-grey">Vancouver is home to some of the world's leading AI research. We translate that research-level innovation into production systems that deliver measurable business impact.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div {...fadeIn} transition={{ delay: 0.2 }} className="bg-gradient-to-br from-gray-50 to-emerald-50 rounded-3xl p-10">
              <div className="space-y-8">
                <div>
                  <div className="text-4xl font-bold text-dark-slate">15+</div>
                  <div className="text-muted-grey mt-1">Years Engineering Leadership</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-dark-slate">50+</div>
                  <div className="text-muted-grey mt-1">Enterprise Engagements</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-dark-slate">3</div>
                  <div className="text-muted-grey mt-1">Major Cloud Platforms (AWS, GCP, Azure)</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-dark-slate">30-50%</div>
                  <div className="text-muted-grey mt-1">Avg Cloud Cost Reduction</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Also Serving Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-muted-grey mb-4">Also serving businesses across Canada</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/calgary" className="px-6 py-2 bg-white rounded-full text-dark-slate font-medium border border-gray-200 hover:border-dark-slate transition-colors">
              Calgary
            </Link>
            <span className="px-6 py-2 bg-white rounded-full text-dark-slate font-medium border border-gray-200">
              Toronto
            </span>
            <span className="px-6 py-2 bg-white rounded-full text-dark-slate font-medium border border-gray-200">
              Edmonton
            </span>
            <span className="px-6 py-2 bg-white rounded-full text-dark-slate font-medium border border-gray-200">
              All of Canada
            </span>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-20 bg-dark-slate text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeIn}>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Vancouver Business?
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
              Whether you need a fractional CTO, AI strategy, cloud migration, or data analytics—book a free strategy call and let's discuss how we can accelerate your technology roadmap.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://calendar.app.google/ube4SfQSbn6SqRCCA"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white text-dark-slate rounded-full font-semibold text-lg hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2"
              >
                Book a Strategy Call
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </motion.a>
              <a
                href="mailto:info@northinteractive.co"
                className="px-8 py-4 border-2 border-white text-white rounded-full font-semibold text-lg hover:bg-white hover:text-dark-slate transition-colors inline-flex items-center justify-center"
              >
                Email Us Directly
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default VancouverPage;
